const state = {
    wsNotify: {},
    wsMess: {},
    wsState: false,
    socket: null,
    wsChang: null,
    // WebSocket 管理相關狀態
    heartbeatTimer: null,
    reconnectTimer: null,
    connectTimeoutTimer: null,
    isConnecting: false,
    manualClose: false,
    reconnectCount: 0,
    maxReconnectCount: 10,
    connectTimeout: 10000
};

const actions = {
    /**
     * 初始化 WebSocket（全局單例）
     */
    initWebsocket({ state, commit, rootState }) {
        // 檢查必要的用戶資訊
        const user = rootState.account?.user;
        const status = rootState.account?.status;

        if (!status?.token || !user?.data?.user?.id) {
            console.warn('WebSocket 缺少 Token 或 UserId');
            return;
        }

        // 已經連線中，不重複建立
        if (state.socket && (
            state.socket.readyState === WebSocket.OPEN ||
            state.socket.readyState === WebSocket.CONNECTING
        )) {
            console.log('WebSocket 已存在，不重複建立');
            return;
        }

        // 清除舊的重連計時器
        commit('clearReconnectTimer');

        commit('setManualClose', false);
        commit('setIsConnecting', true);

        const wsURL =
            `${process.env.VUE_APP_WSS}message` +
            `?id=${encodeURIComponent(status.token)}` +
            `&userid=${encodeURIComponent(user.data.user.id)}`;

        console.log('建立全局 WebSocket:', wsURL);

        try {
            const ws = new WebSocket(wsURL);

            commit('setSocket', { itme: ws });

            // 設置連接超時
            const timeoutId = setTimeout(() => {
                if (state.isConnecting && ws.readyState !== WebSocket.OPEN) {
                    console.error('WebSocket 連接超時');
                    ws.close();
                    commit('setIsConnecting', false);
                    commit('reconnectWebSocket');
                }
            }, state.connectTimeout);

            commit('setConnectTimeoutTimer', timeoutId);

            ws.onopen = () => {
                console.log('✅ 全局 WebSocket 連線成功');
                commit('clearConnectTimeoutTimer');
                commit('setIsConnecting', false);
                commit('setReconnectCount', 0);
                commit('setWsState', { state: true });
                commit('startHeartbeat');
            };

            ws.onmessage = (event) => {
                const data = event.data;

                // 心跳回應
                if (data === 'pong' || data === '"pong"' || data.includes('"pong"')) {
                    return;
                }

                try {
                    const message = JSON.parse(data);
                    console.log('📩 WebSocket 收到:', message);
                    commit('setWsNotify', { data: message });
                } catch (error) {
                    console.error('WS JSON 解析錯誤:', error, data);
                }
            };

            ws.onerror = (event) => {
                console.error('❌ WebSocket 連線錯誤', event);
                commit('clearConnectTimeoutTimer');
                commit('setIsConnecting', false);
            };

            ws.onclose = (event) => {
                console.warn(
                    `⚠️ WebSocket 關閉`,
                    `code=${event.code}`,
                    `reason=${event.reason || '無'}`,
                    `wasClean=${event.wasClean}`
                );

                commit('clearConnectTimeoutTimer');
                commit('setIsConnecting', false);
                commit('clearHeartbeat');
                commit('setWsState', { state: false });
                commit('setSocket', { itme: null });

                // 非主動關閉才重連
                if (!state.manualClose && event.code !== 1000 && event.code !== 1001) {
                    commit('reconnectWebSocket');
                }
            };

        } catch (error) {
            console.error('WebSocket 建立失敗:', error);
            commit('setIsConnecting', false);
            commit('reconnectWebSocket');
        }
    },

    /**
     * 主動關閉 WebSocket
     */
    closeWebSocket({ state, commit }) {
        console.log('主動關閉全局 WebSocket');

        commit('setManualClose', true);
        commit('clearHeartbeat');
        commit('clearReconnectTimer');

        if (state.socket) {
            try {
                if (
                    state.socket.readyState === WebSocket.OPEN ||
                    state.socket.readyState === WebSocket.CONNECTING
                ) {
                    state.socket.close(1000, 'client close');
                }
            } catch (error) {
                console.error('關閉 WebSocket 失敗:', error);
            }
        }

        commit('setSocket', { itme: null });
        commit('setIsConnecting', false);
        commit('setWsState', { state: false });
    }
};

const mutations = {
    setWsNotify(state, payload) {
        state.wsNotify = payload;
    },

    setWsMess(state, payload) {
        state.wsMess = payload;
    },

    setWsState(state, payload) {
        state.wsState = payload.state;
    },

    setSocket(state, payload) {
        state.socket = payload.itme;
    },

    setChang(state, payload) {
        state.wsChang = payload.id;
    },

    setManualClose(state, value) {
        state.manualClose = value;
    },

    setIsConnecting(state, value) {
        state.isConnecting = value;
    },

    setReconnectCount(state, value) {
        state.reconnectCount = value;
    },

    setConnectTimeoutTimer(state, timer) {
        state.connectTimeoutTimer = timer;
    },

    sendMessage(state, payload) {
        if (state.socket && state.socket.readyState === WebSocket.OPEN) {
            const data = typeof payload === 'string' ? payload : JSON.stringify(payload);
            state.socket.send(data);
        } else {
            console.error('WebSocket 未連接，無法發送訊息');
            state.wsState = false;
        }
    },

    /**
     * 啟動心跳
     */
    startHeartbeat(state) {
        // 清除舊的心跳
        if (state.heartbeatTimer) {
            clearInterval(state.heartbeatTimer);
        }

        state.heartbeatTimer = setInterval(() => {
            if (state.socket && state.socket.readyState === WebSocket.OPEN) {
                try {
                    state.socket.send(JSON.stringify({ type: 'ping' }));
                } catch (error) {
                    console.error('WebSocket 心跳發送失敗:', error);
                }
            }
        }, 25000);
    },

    /**
     * 清除心跳
     */
    clearHeartbeat(state) {
        if (state.heartbeatTimer) {
            clearInterval(state.heartbeatTimer);
            state.heartbeatTimer = null;
        }
    },

    /**
     * WebSocket 重連
     */
    reconnectWebSocket(state) {
        if (state.manualClose) {
            return;
        }

        // 已經有重連排程
        if (state.reconnectTimer) {
            return;
        }

        // 已經有連線
        if (state.socket && (
            state.socket.readyState === WebSocket.OPEN ||
            state.socket.readyState === WebSocket.CONNECTING
        )) {
            return;
        }

        if (state.reconnectCount >= state.maxReconnectCount) {
            console.error('WebSocket 已達最大重連次數');
            return;
        }

        state.reconnectCount++;

        const delay = Math.min(
            3000 * Math.pow(2, state.reconnectCount - 1),
            30000
        );

        console.log(
            `WebSocket ${delay / 1000} 秒後重新連線`,
            `第 ${state.reconnectCount} 次`
        );

        state.reconnectTimer = setTimeout(() => {
            state.reconnectTimer = null;
            // 需要通過 dispatch 來調用 action
            this.dispatch('ws/initWebsocket');
        }, delay);
    },

    /**
     * 清除重連計時器
     */
    clearReconnectTimer(state) {
        if (state.reconnectTimer) {
            clearTimeout(state.reconnectTimer);
            state.reconnectTimer = null;
        }
    },

    /**
     * 清除連接超時計時器
     */
    clearConnectTimeoutTimer(state) {
        if (state.connectTimeoutTimer) {
            clearTimeout(state.connectTimeoutTimer);
            state.connectTimeoutTimer = null;
        }
    }
};

const getters = {
    isConnected: state => {
        return state.socket && state.socket.readyState === WebSocket.OPEN;
    }
};

export const ws = {
    namespaced: true,
    state,
    actions,
    mutations,
    getters,
}

/* 因爲將多個 module 併入一個 Vuex Store 時，actions 及 mutations 都是在 root space 中，
所以一般會為了避免 actions / mutations 的命名衝突，會自己加上 namespace  : true
*/